import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Order, OrderStatus, Restaurant } from '@/types';

// 판매자 대시보드 매출 정보
export interface SalesMetrics {
  monthlyRevenue: number;
  monthlySalesCount: number;
  currentMonth: string;
}

// 주문 상세 정보 (주문 아이템 포함)
export interface OrderWithItems extends Order {
  buyer_nickname: string;
  order_number: string;
  order_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER'; // Database field name
  accepted_at?: string; // 주문 수락 시간
  cancellation_reason?: string; // 취소 사유
  auto_cancel_at?: string; // 자동 취소 시간
  items: Array<{
    id: string;
    order_id: string;
    product_id: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    products?: {
      name: string;
    };
    // Legacy compatibility - computed fields
    product_name?: string;
    price?: number;
  }>;
  restaurant?: Restaurant;
}

// 판매자 상태 타입
export interface SellerState {
  // 대시보드 데이터
  salesMetrics: SalesMetrics | null;
  reviewCount: number;
  businessStatus: boolean;
  
  // 주문 관리
  orders: Record<OrderStatus, OrderWithItems[]>;
  orderCounts: Record<OrderStatus, number>;
  activeOrderTab: OrderStatus;
  selectedOrder: OrderWithItems | null;
  
  // UI 상태
  loading: boolean;
  error: string | null;
  
  // Actions
  setSalesMetrics: (metrics: SalesMetrics) => void;
  setReviewCount: (count: number) => void;
  setBusinessStatus: (status: boolean) => void;
  setOrders: (status: OrderStatus, orders: OrderWithItems[]) => void;
  setOrderCounts: (counts: Record<OrderStatus, number>) => void;
  updateOrderCount: (status: OrderStatus, count: number) => void;
  setActiveOrderTab: (tab: OrderStatus) => void;
  setSelectedOrder: (order: OrderWithItems | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // 주문 상태 변경
  updateOrderStatus: (orderId: string, newStatus: OrderStatus) => void;
  
  // 주문 카운트 가져오기
  getOrderCount: (status: OrderStatus) => number;
  
  // 초기화
  reset: () => void;

  // Legacy support for existing code
  storeInfo: any;
  setStoreInfo: (info: any) => void;
  products: any[];
  setProducts: (products: any[]) => void;
  addProduct: (product: any) => void;
  updateProduct: (productId: string, updates: any) => void;
  deleteProduct: (productId: string) => void;
}

const initialState = {
  salesMetrics: null,
  reviewCount: 0,
  businessStatus: true,
  orders: {
    PENDING: [],
    ACCEPTED: [],
    READY: [],
    COMPLETED: [],
    CANCELLED: [],
    NOSHOW: []
  } as Record<OrderStatus, OrderWithItems[]>,
  orderCounts: {
    PENDING: 0,
    ACCEPTED: 0,
    READY: 0,
    COMPLETED: 0,
    CANCELLED: 0,
    NOSHOW: 0
  } as Record<OrderStatus, number>,
  activeOrderTab: 'PENDING' as OrderStatus,
  selectedOrder: null,
  loading: false,
  error: null,
  storeInfo: null,
  products: [],
};

export const useSellerStore = create<SellerState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      setSalesMetrics: (metrics) => set({ salesMetrics: metrics }),
      setReviewCount: (count) => set({ reviewCount: count }),
      setBusinessStatus: (status) => set({ businessStatus: status }),
      
      setOrders: (status, orders) => set((state) => ({
        orders: {
          ...state.orders,
          [status]: orders
        }
      })),
      
      setOrderCounts: (counts) => set({ orderCounts: counts }),
      
      updateOrderCount: (status, count) => set((state) => ({
        orderCounts: {
          ...state.orderCounts,
          [status]: count
        }
      })),
      
      setActiveOrderTab: (tab) => set({ activeOrderTab: tab }),
      setSelectedOrder: (order) => set({ selectedOrder: order }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      
      updateOrderStatus: (orderId, newStatus) => set((state) => {
        // 모든 상태에서 해당 주문을 찾아서 이동
        const allOrders = Object.values(state.orders).flat();
        const order = allOrders.find(o => o.id === orderId);
        
        if (!order) return state;
        
        // 업데이트된 주문 객체
        const updatedOrder = { ...order, status: newStatus };
        
        // 새로운 orders 객체 생성
        const newOrders = { ...state.orders };
        
        // 기존 위치에서 제거
        Object.keys(newOrders).forEach(status => {
          newOrders[status as OrderStatus] = newOrders[status as OrderStatus].filter(o => o.id !== orderId);
        });
        
        // 새로운 위치에 추가
        newOrders[newStatus] = [...newOrders[newStatus], updatedOrder];
        
        return { orders: newOrders };
      }),
      
      getOrderCount: (status) => {
        const state = get();
        // 저장된 카운트 우선 사용, 없으면 배열 길이 사용
        return state.orderCounts[status] ?? state.orders[status]?.length ?? 0;
      },
      
      reset: () => set(initialState),

      // Legacy support
      setStoreInfo: (info) => set({ storeInfo: info }),
      setProducts: (products) => set({ products }),
      addProduct: (product) => set((state) => ({ 
        products: [...state.products, product] 
      })),
      updateProduct: (productId, updates) => set((state) => ({
        products: state.products.map(p => 
          p.id === productId ? { ...p, ...updates } : p
        )
      })),
      deleteProduct: (productId) => set((state) => ({
        products: state.products.filter(p => p.id !== productId)
      })),
    }),
    {
      name: 'seller-store',
      partialize: (state) => ({
        businessStatus: state.businessStatus,
        activeOrderTab: state.activeOrderTab,
      }),
    }
  )
);